import {Input, Select, Spin} from 'antd';
import debounce from 'lodash.debounce';
import {inject, observer} from 'mobx-react';
import * as React from 'react';
import {RouteComponentProps} from 'react-router';
import Card, {CardBody, CardFooter, CardHeader} from '../../components/Card';
import ChatFeed from '../../components/ChatUI/ChatFeed';
import Flex from '../../components/Flex';
import {Main, Sider, Wrapper} from '../../components/Layout';
import Persona from '../../components/Persona';
import PersonaStack from '../../components/PersonaStack';
import Text from '../../components/Text';
import ChatList, {KindOfChatType} from '../../containers/ChatList';
import {Auth} from '../../models/authModel';
import {ChatChannelKind} from '../../models/chatChannelModel';
import {ChatModelType} from '../../models/chatModel/chatModel';
import {searchListings, searchTeams} from '../../services/api';

type ChannelRouteParams = {
  channelKind: string;
  channelId: string;
};

interface WithInjectedProps extends RouteComponentProps<ChannelRouteParams> {
  chat: ChatModelType;
  auth: Auth;
}

@inject('chat', 'auth')
@observer
export default class Channel extends React.Component<
  RouteComponentProps,
  {
    isFetchingTeams: boolean;
    // tslint:disable-next-line: no-any
    teamsData: any;
    filterByTeamId: number | null;

    isFetchingListings: boolean;
    // tslint:disable-next-line: no-any
    listingsData: any;
    filterByListingId: number | null;

    searchQuery: string;
    searchByField: 'message' | 'guest_name';
  }
> {
  lastFetchId: number;

  public constructor(props: WithInjectedProps) {
    super(props);
    this.lastFetchId = 0;
    this.state = {
      isFetchingTeams: false,
      teamsData: [],
      filterByTeamId: null,

      isFetchingListings: false,
      listingsData: [],
      filterByListingId: null,

      searchQuery: '',
      searchByField: 'message'
    };
  }

  public componentDidMount() {
    const {loadChannels, setCurrentChannel} = this._withInjectedProps.chat;

    loadChannels(this._channelKind);
    setCurrentChannel(this._channelId);
  }

  public componentWillUnmount() {
    this._withInjectedProps.chat.unsetCurrentChannel();
  }

  public getSnapshotBeforeUpdate(nextProps: WithInjectedProps) {
    const currentChannelId = this._channelId;
    const nextChannelId = Number(nextProps.match.params.channelId);

    if (nextChannelId && currentChannelId !== nextChannelId) {
      this._withInjectedProps.chat.setCurrentChannel(Number(nextChannelId));
    }

    return null;
  }

  public componentDidUpdate() {
    if (
      this.state.filterByListingId ||
      this.state.filterByTeamId ||
      this.state.searchQuery
    ) {
      this._withInjectedProps.chat.clearFilterPagination();
      this._withInjectedProps.chat.filterChannels({
        teamId: this.state.filterByTeamId,
        listingId: this.state.filterByListingId,
        q: this.state.searchQuery,
        searchType: this.state.searchByField
        // ...(this.state.searchQuery && {
        //   q: this.state.searchQuery,
        //   searchType: this.state.searchByField
        // })
      });
    }
  }

  _fetchTeams = (searchStr: string) => {
    // tslint:disable-next-line: no-console
    console.log('fetching teams', searchStr);
    this.lastFetchId += 1;
    const fetchId = this.lastFetchId;
    this.setState({teamsData: [], isFetchingTeams: true});

    searchTeams(searchStr).then(({data}) => {
      if (fetchId !== this.lastFetchId) {
        // for fetch callback order
        return;
      }
      // tslint:disable-next-line: no-any
      const teamsData = (data.teams || []).map((team: any) => ({
        text: team.name,
        value: team.id
      }));
      this.setState({teamsData, isFetchingTeams: false});
    });
  };

  _setFilterByTeamId = (teamId: number) => {
    this.setState({filterByTeamId: teamId, filterByListingId: null});
  };

  _fetchListings = (searchStr: string) => {
    // tslint:disable-next-line: no-console
    console.log('fetching listings', searchStr);
    this.lastFetchId += 1;
    const fetchId = this.lastFetchId;
    this.setState({listingsData: [], isFetchingListings: true});

    searchListings(searchStr, this.state.filterByTeamId!).then(
      // tslint:disable-next-line: no-any
      ({data}: any) => {
        if (fetchId !== this.lastFetchId) {
          // for fetch callback order
          return;
        }
        // tslint:disable-next-line: no-any
        const listingsData = (data.listings || []).map((listing: any) => ({
          text: listing.name,
          value: listing.id
        }));
        this.setState({listingsData, isFetchingTeams: false});
      }
    );
  };

  _setFilterByListingId = (listingId: number) => {
    this.setState({filterByListingId: listingId});
  };

  handleSearchFieldSelect = (field: string) => {
    if (field === 'message') {
      this.setState({searchByField: field});
    }
  };

  public render() {
    const {
      chat: {currentChannel},
      auth
    } = this._withInjectedProps;

    return (
      <Wrapper style={{height: 'calc(100vh - 114px)'}}>
        <Sider
          style={{
            height: '100%',
            width: 360,
            flexBasis: 360,
            overflowX: 'hidden'
          }}
        >
          <Card style={{height: '100%', borderRight: '1px solid #F1F3F6'}}>
            <CardHeader style={{padding: 10}}>Guest Messages</CardHeader>
            <CardBody style={{height: 'calc(100% - 59px)', padding: 0}}>
              <Flex>
                <Select
                  showSearch={true}
                  placeholder="Select team"
                  notFoundContent={
                    this.state.isFetchingTeams ? <Spin size="small" /> : null
                  }
                  filterOption={false}
                  onSearch={debounce(this._fetchTeams, 500)}
                  onSelect={this._setFilterByTeamId}
                  style={{width: '50%', flex: 0, flexBasis: '50%'}}
                >
                  {/*  tslint:disable-next-line: no-any */}
                  {this.state.teamsData.map((d: any) => (
                    <Select.Option key={d.value}>{d.text}</Select.Option>
                  ))}
                </Select>

                <Select
                  disabled={!this.state.filterByTeamId}
                  showSearch={true}
                  placeholder="Select listing"
                  notFoundContent={
                    this.state.isFetchingListings ? (
                      <Spin size="small" />
                    ) : null
                  }
                  filterOption={false}
                  onSearch={debounce(this._fetchListings, 500)}
                  onSelect={this._setFilterByListingId}
                  style={{width: '50%', flexGrow: 0, flexBasis: '50%'}}
                >
                  {/*  tslint:disable-next-line: no-any */}
                  {this.state.listingsData.map((d: any) => (
                    <Select.Option key={d.value}>{d.text}</Select.Option>
                  ))}
                </Select>
              </Flex>
              <Flex>
                <Select
                  style={{flexBasis: 120}}
                  onSelect={this.handleSearchFieldSelect}
                  defaultValue="message"
                >
                  <Select.Option title="By message" value="message">
                    By message
                  </Select.Option>
                </Select>
                <Input.Search
                  placeholder={`Enter ${this.state.searchByField}`}
                  onSearch={value => this.setState({searchQuery: value})}
                  allowClear={true}
                  style={{width: 300}}
                />
              </Flex>

              <ChatList
                loadMore={this._loadMoreChannels}
                kindOfChat={KindOfChatType.GUEST_CHAT}
                filteredResults={this._withInjectedProps.chat.filterResults}
                filtered={Boolean(
                  this.state.filterByListingId ||
                    this.state.filterByTeamId ||
                    this.state.searchQuery
                )}
              />
            </CardBody>
          </Card>
        </Sider>
        <Main style={{height: '100%'}}>
          <Card style={{height: '100%', padding: 0}}>
            <CardHeader>
              <Flex style={{height: '100%'}}>
                {currentChannel &&
                currentChannel.thread &&
                currentChannel.thread.listing &&
                currentChannel.thread.listing.previewImageUrl ? (
                  <div style={{marginRight: 12}}>
                    <img
                      src={currentChannel.thread.listing.previewImageUrl}
                      style={{width: 87, display: 'block', borderRadius: 3}}
                    />
                  </div>
                ) : null}
                <Flex direction="column" justify="space-between">
                  <h5>{currentChannel && currentChannel.title}</h5>
                  <div>
                    {currentChannel &&
                    currentChannel.thread &&
                    currentChannel.thread.listing &&
                    currentChannel.thread.listing.internalName ? (
                      <Text
                        color="secondary"
                        fontSize={12}
                        fontWeight={400}
                        style={{marginRight: 8}}
                      >
                        {currentChannel.thread.listing.internalName}
                      </Text>
                    ) : null}
                    {/* <Text fontSize={12} color="#7E8DA6" fontWeight={400}>
                      {currentChannel && currentChannel.thread
                        ? `Check in: ${
                            currentChannel.thread.lastDates[0]
                          } - Check out: ${currentChannel.thread.lastDates[1]}`
                        : null}
                    </Text> */}
                  </div>
                  <Text color="primary" fontSize={12} fontWeight={400}>
                    <PersonaStack maxLength={3}>
                      {currentChannel &&
                        currentChannel.users.map((u, idx) => (
                          <Persona key={idx}>{u.name}</Persona>
                        ))}
                    </PersonaStack>
                  </Text>
                </Flex>
              </Flex>
            </CardHeader>
            <CardBody
              style={{height: 'calc(100% - 55px - 99.02px)', paddingLeft: 20}}
            >
              {currentChannel && auth.profile ? (
                <ChatFeed
                  messages={this._messages}
                  currentUserId={this._currentUserId}
                  loadMore={this._loadMoreMessages}
                  loadMoreOffsetHeight={10}
                  showSenderName={true}
                />
              ) : null}
            </CardBody>
            <CardFooter style={{padding: 0, backgroundColor: '#F5F6CE'}}>
              <Flex direction="column" justify="center">
                <Text
                  style={{padding: 10, textAlign: 'center', color: '#AA0000'}}
                >
                  Only view in admin view
                </Text>
              </Flex>

              {/* <ChatForm channel={currentChannel} /> */}
            </CardFooter>
          </Card>
        </Main>
      </Wrapper>
    );
  }

  private get _messages() {
    if (!this._withInjectedProps.chat.currentChannel) {
      return [];
    }

    return this._withInjectedProps.chat.currentChannel.sortedMessages.map(
      m => ({
        format: m.format,
        senderId: `${m.senderId}_${m.senderName}`,
        senderName: m.senderName,
        senderAvatar: m.senderAvatar,
        createdAt: m.createdAt
      })
    );
  }

  private get _currentUserId() {
    const {id, firstName, fullName} = this._withInjectedProps.auth.profile!;
    return `${id}_${fullName || firstName}`;
  }

  private get _channelId() {
    return Number(this._withInjectedProps.match.params.channelId);
  }

  private get _channelKind() {
    return this._withInjectedProps.match.params.channelKind as ChatChannelKind;
  }

  private get _withInjectedProps() {
    return this.props as WithInjectedProps;
  }

  private _loadMoreMessages = () =>
    this._withInjectedProps.chat.currentChannel!.loadPreviousCachedMessages();

  private _loadMoreChannels = () => {
    const {
      chat: {loadChannels, nextOffset, filterChannels, nextFilterOffset}
    } = this._withInjectedProps;

    if (
      this.state.filterByListingId ||
      this.state.filterByTeamId ||
      this.state.searchQuery
    ) {
      filterChannels(
        {
          listingId: this.state.filterByListingId,
          teamId: this.state.filterByTeamId,
          q: this.state.searchQuery,
          searchType: this.state.searchByField
        },
        this._channelKind,
        nextFilterOffset
      );
    } else {
      loadChannels(this._channelKind, nextOffset);
    }
  };
}



// WEBPACK FOOTER //
// ./src/pages/Channel/Channel.tsx